import { MutableRefObject, useState, useEffect, useRef } from 'react';
import { useProjectsStore, useUIStore } from '@/stores';
import { useProjects } from '@/hooks/project/useProjects';
import { useResponsiveLayout } from '@/hooks/ui/useResponsiveLayout';
import { useHashNavigate } from '@/contexts/HashRouterContext';
import { apiPost } from '@/lib/api';
import PDFList from '@/components/PDFList';
import AgentsList from '@/components/AgentsList';
import ProjectsList from '@/pages/ProjectsList';
import ProjectMembers from '@/components/ProjectMembers';
import ChatsPage from '@/pages/ChatsPage';
import SharedPage from '@/pages/SharedPage';
import ProjectDetail from '@/components/ProjectDetail';
import ChatPage from '@/components/Chat';
import ArtifactViewer from '@/components/ArtifactViewer';
import SkillTaskDashboard from '@/components/SkillTaskDashboard';
import { useSkillTaskStore } from '@/stores/skillTaskStore';
import { useConversationsStore } from '@/stores/conversationsStore';
import NewProjectModal from '@/components/NewProjectModal';
import UserSettings from '@/components/UserSettings';
import { ProjectFormData } from '@/types';
import { showToast } from '@/components/Toast';

interface MainContentProps {
  chatReadOnly: boolean;
  sharedConversationRecordId: number | null;
  chatPageSelectedConversationId: string | undefined;
  shareConversationId: string | null;
  stopRequestRef: MutableRefObject<(() => void) | null>;
  showMembersForProject: { projectId: string; projectName: string } | null;
  isUploadingFiles: boolean;
  onSetChatReadOnly: (readOnly: boolean) => void;
  onSetSharedConversationRecordId: (id: number | null) => void;
  onSetChatPageSelectedConversationId: (id: string | undefined) => void;
  onSetShowMembersForProject: (
    project: { projectId: string; projectName: string } | null
  ) => void;
  onShowSharedList: () => void;
  onHandleNewProject: () => void;
  onHandleStarProject: (projectId: string) => Promise<void>;
  onHandleDeleteProject: (
    projectId: string,
    role: string | undefined
  ) => Promise<void>;
  onHandleShowPDFList: () => void;
  onHandleProjectConversationSelect: (
    conversationId: string,
    projectId: string
  ) => void;
  onHandleSwitchToChat: (
    conversationId: string,
    projectId: string,
    messageDetails?: {
      content: string;
      isRetry?: boolean;
      options?: {
        usePublic?: boolean;
        allProjects?: boolean;
        thinkType?: 'think' | 'no-think' | 'hard-think';
        clientId?: string | null;
        pendingId?: string; // 🔥 架构简化：移到 options 中，可选
      };
    }
  ) => void;
  onHandleBackToProjects: () => void;
  onHandleRenameProject: (projectId: string, newName: string) => Promise<void>;
  onHandleUpdateProject: (project: {
    id: string;
    name: string;
    description: string;
    instructions: string;
  }) => Promise<void>;
  onHandleProjectSelect: (projectId: string | null) => void;
  onRefreshConversations: () => Promise<void>;
  uploadProjectFiles: (projectId: string, files: File[]) => Promise<void>;
}

export default function MainContent({
  chatReadOnly,
  sharedConversationRecordId,
  chatPageSelectedConversationId,
  shareConversationId,
  stopRequestRef,
  showMembersForProject,
  isUploadingFiles,
  onSetChatReadOnly,
  onSetSharedConversationRecordId,
  onSetChatPageSelectedConversationId,
  onSetShowMembersForProject,
  onShowSharedList,
  onHandleNewProject,
  onHandleStarProject,
  onHandleDeleteProject,
  onHandleShowPDFList,
  onHandleProjectConversationSelect,
  onHandleSwitchToChat,
  onHandleBackToProjects,
  onHandleRenameProject,
  onHandleUpdateProject,
  onHandleProjectSelect,
  onRefreshConversations,
  uploadProjectFiles,
}: MainContentProps) {
  const { currentProjectId, projects } = useProjectsStore();
  const isDashboardOpen = useSkillTaskStore(state => state.isDashboardOpen);
  const currentConversationId = useConversationsStore(
    state => state.currentConversationId
  );
  const {
    currentView,
    artifactViewer,
    generalUsePublic,
    generalAllProjects,
    projectUsePublic,
    projectAllProjects,
    projectsListInitialTab,
    setProjectsListInitialTab,
    setFilePreview,
    isMobileFullscreen,
  } = useUIStore();

  const navigate = useHashNavigate();

  const { loadProjects } = useProjects();
  const { isProjectsLoading, hasLoadedProjects } = useProjectsStore();
  const { isLargeScreen } = useResponsiveLayout();

  // 🔥 Close file preview when view changes (e.g., navigating from chat to shared page)
  const prevViewRef = useRef(currentView);
  useEffect(() => {
    if (prevViewRef.current !== currentView) {
      setFilePreview(null);
      prevViewRef.current = currentView;
    }
  }, [currentView, setFilePreview]);

  // 🔥 直接从已订阅的 projects 数组中查找 currentProject
  // 这确保当 projects 更新时（如 archive 后），组件会重新渲染并获取最新数据
  const currentProject = projects.find(p => p.id === currentProjectId);

  // 编辑项目弹窗状态
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [editingProjectData, setEditingProjectData] =
    useState<ProjectFormData | null>(null);

  // 处理编辑项目
  const handleEditProject = (projectId: string) => {
    const { projects } = useProjectsStore.getState();
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setEditingProjectData({
        id: project.id,
        name: project.name,
        description: project.description || '',
        instructions: project.instructions || '',
      });
      setIsEditProjectModalOpen(true);
    }
  };

  return (
    <div
      className={`flex-1 flex flex-col min-w-0 min-h-0 ${isMobileFullscreen ? 'pb-0' : 'pb-12'} lg:pb-0 relative ${isUploadingFiles ? 'pointer-events-none' : ''}`}
    >
      {/* Mobile Header is now handled by each page's own MobileHeader component */}

      {/* PDF List View */}
      {currentView === 'pdf-list' && (
        <PDFList projectId={currentProject?.id || null} />
      )}

      {/* Agents List View */}
      {currentView === 'agents-list' && <AgentsList />}

      {/* Projects List View */}
      {currentView === 'projects-list' && !showMembersForProject && (
        <ProjectsList
          initialTab={projectsListInitialTab}
          onProjectSelect={projectId => {
            setProjectsListInitialTab('active');
            navigate({ type: 'project', projectId });
            // Refresh projects to update lastVisitedAt and badge count
            setTimeout(() => {
              loadProjects();
            }, 500);
          }}
          onNewProject={onHandleNewProject}
          onStarProject={onHandleStarProject}
          onEditProject={handleEditProject}
          onManageMembers={(projectId, projectName) => {
            onSetShowMembersForProject({ projectId, projectName });
          }}
          onDeleteProject={onHandleDeleteProject}
          onArchiveProject={async (projectId, archived) => {
            const { archiveProject } =
              await import('@/services/projectService');

            // 🔥 先检查项目是否 starred（在 archive 之前检查）
            // const wasStarred = archived
            //   ? useProjectsStore
            //       .getState()
            //       .projects.find(p => p.id === projectId)?.starred
            //   : false;

            // 🔥 第一步：先执行 Archive
            await archiveProject(projectId, archived);

            // 🔥 第二步：Archive 完成后，如果项目之前是 starred，取消 star
            // conversation 的 unstar 由后端处理
            // if (archived && wasStarred) {
            //   try {
            //     await apiPost(`/api/projects/${projectId}/star`, {
            //       starred: false,
            //     });
            //   } catch (err) {
            //     console.error('Failed to unstar project:', err);
            //   }
            // }

            // 🔥 第三步：刷新 projects 列表
            await loadProjects();

            // 🔥 第四步：刷新侧边栏（包括 Starred 列表和 Recent chats）
            if (archived) {
              window.refreshSidebarStarred?.();
              window.refreshSidebarRecent?.();
            }
          }}
        />
      )}

      {/* Project Members View */}
      {currentView === 'projects-list' && showMembersForProject && (
        <ProjectMembers
          projectId={showMembersForProject.projectId}
          projectName={showMembersForProject.projectName}
          projectArchived={
            projects.find(p => p.id === showMembersForProject.projectId)
              ?.archived || false
          }
          onBack={() => onSetShowMembersForProject(null)}
        />
      )}

      {/* Chats List View */}
      {currentView === 'chats-list' && (
        <ChatsPage
          onConversationSelect={conversationId => {
            onSetChatReadOnly(false);
            onSetSharedConversationRecordId(null);
            navigate({ type: 'chat', conversationId });
          }}
          onNewChat={() => {
            onSetChatReadOnly(false);
            onSetSharedConversationRecordId(null);
            navigate({ type: 'chat' });
          }}
        />
      )}

      {/* Shared List View */}
      {currentView === 'shared-list' && (
        <SharedPage
          onOpenSharedConversation={sharedId => {
            onSetChatPageSelectedConversationId(undefined);
            onSetChatReadOnly(true);
            onSetSharedConversationRecordId(sharedId);
            navigate({ type: 'chat' });
            // Refresh shared count badge after viewing
            setTimeout(() => {
              window.refreshSidebarSharedCount?.();
            }, 500);
          }}
          onOpenConversation={conversationId => {
            onSetSharedConversationRecordId(null);
            onSetChatReadOnly(false);
            navigate({ type: 'chat', conversationId });
          }}
          onRefreshConversations={onRefreshConversations}
        />
      )}

      {/* User Settings View */}
      {currentView === 'user-settings' && (
        <UserSettings
          isOpen={true}
          onClose={() => {
            navigate({ type: 'chat' });
          }}
        />
      )}

      {/* Project Detail View */}
      {currentView === 'project-detail' && currentProject && (
        <ProjectDetail
          projectId={currentProject.id}
          projectName={currentProject.name}
          projectRole={currentProject.role}
          isUploadingFiles={isUploadingFiles}
          projectDescribe={currentProject.description || ''}
          projectInstructions={currentProject.instructions || ''}
          projectStarred={currentProject.starred || false}
          projectArchived={currentProject.archived || false}
          onFileUpload={files => uploadProjectFiles(currentProject.id, files)}
          onShowProjectFiles={onHandleShowPDFList}
          onConversationSelect={onHandleProjectConversationSelect}
          onSwitchToChat={onHandleSwitchToChat}
          onBackToProjects={onHandleBackToProjects}
          onRenameProject={onHandleRenameProject}
          onUpdateProject={project => onHandleUpdateProject(project)}
          onStarProject={async () => {
            await loadProjects();
          }}
          onArchiveProject={async (projectId, archived) => {
            const { archiveProject } =
              await import('@/services/projectService');

            // 🔥 先检查项目是否 starred（在 archive 之前检查）
            const wasStarred = archived
              ? useProjectsStore
                  .getState()
                  .projects.find(p => p.id === projectId)?.starred
              : false;

            // 🔥 第一步：先执行 Archive
            await archiveProject(projectId, archived);

            // 🔥 第二步：Archive 完成后，如果项目之前是 starred，取消 star
            // conversation 的 unstar 由后端处理
            if (archived && wasStarred) {
              try {
                await apiPost(`/api/projects/${projectId}/star`, {
                  starred: false,
                });
              } catch (err) {
                console.error('Failed to unstar project:', err);
              }
            }

            // 🔥 第三步：刷新 projects 列表
            await loadProjects();

            // 🔥 第四步：刷新侧边栏（包括 Starred 列表和 Recent chats）
            if (archived) {
              window.refreshSidebarStarred?.();
              window.refreshSidebarRecent?.();
              setProjectsListInitialTab('archived');
              navigate({ type: 'projects' });
            }
          }}
          usePublic={
            projectUsePublic[currentProject.id] !== undefined
              ? projectUsePublic[currentProject.id]
              : true
          }
          allProjects={
            projectAllProjects[currentProject.id] !== undefined
              ? projectAllProjects[currentProject.id]
              : true
          }
        />
      )}

      {/* Project loading or not found */}
      {currentView === 'project-detail' && !currentProject && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            {isProjectsLoading || !hasLoadedProjects ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Project not found
              </h3>
            )}
          </div>
        </div>
      )}

      {/* 🔥 Project Members View (route-based) */}
      {currentView === 'project-members' && currentProject && (
        <ProjectMembers
          projectId={currentProject.id}
          projectName={currentProject.name}
          projectArchived={currentProject.archived || false}
          onBack={() =>
            navigate({ type: 'project', projectId: currentProject.id })
          }
        />
      )}

      {/* Project Members loading or not found */}
      {currentView === 'project-members' && !currentProject && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            {isProjectsLoading || !hasLoadedProjects ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Project not found
              </h3>
            )}
          </div>
        </div>
      )}

      {/* Chat View with Artifact Split */}
      {currentView === 'chat' && (
        <div
          className={`flex w-full h-full min-w-0 ${
            (artifactViewer || isDashboardOpen) && isLargeScreen
              ? 'flex-row'
              : 'flex-col flex-1'
          }`}
        >
          <div className="flex-1 flex flex-col min-w-0 h-full">
            <ChatPage
              projectId={currentProjectId}
              projectName={currentProject?.name || null}
              shareConversationId={shareConversationId ?? undefined}
              selectedConversationId={chatPageSelectedConversationId}
              sharedConversationRecordId={sharedConversationRecordId}
              readOnly={chatReadOnly}
              onBackToShared={onShowSharedList}
              stopRequestRef={stopRequestRef}
              usePublic={
                currentProjectId
                  ? (projectUsePublic[currentProjectId] ?? true)
                  : generalUsePublic
              }
              allProjects={
                currentProjectId
                  ? (projectAllProjects[currentProjectId] ?? true)
                  : generalAllProjects
              }
              onProjectClick={onHandleProjectSelect}
            />
          </div>
          {artifactViewer && (
            <ArtifactViewer mode={isLargeScreen ? 'split' : 'overlay'} />
          )}
          {isDashboardOpen && !artifactViewer && (
            <SkillTaskDashboard
              mode={isLargeScreen ? 'split' : 'overlay'}
              conversationId={currentConversationId}
            />
          )}
        </div>
      )}

      {/* Hidden ChatPage to preserve message handlers */}
      {currentView !== 'chat' && (
        <div style={{ display: 'none' }}>
          <ChatPage
            projectId={currentProjectId}
            shareConversationId={shareConversationId ?? undefined}
            selectedConversationId={chatPageSelectedConversationId}
            sharedConversationRecordId={null}
            readOnly={chatReadOnly}
            onBackToShared={onShowSharedList}
            stopRequestRef={stopRequestRef}
            usePublic={
              currentProjectId
                ? (projectUsePublic[currentProjectId] ?? true)
                : generalUsePublic
            }
            allProjects={
              currentProjectId
                ? (projectAllProjects[currentProjectId] ?? true)
                : generalAllProjects
            }
            onProjectClick={onHandleProjectSelect}
          />
        </div>
      )}

      {/* Edit Project Modal */}
      {editingProjectData && (
        <NewProjectModal
          isOpen={isEditProjectModalOpen}
          onClose={() => {
            setIsEditProjectModalOpen(false);
            setEditingProjectData(null);
          }}
          mode="edit"
          projectData={editingProjectData}
          onUpdateProject={async project => {
            await onHandleUpdateProject(project);
            setIsEditProjectModalOpen(false);
            setEditingProjectData(null);
            await loadProjects();
          }}
          onShowToast={(message, type) => showToast(message, type)}
        />
      )}
    </div>
  );
}
